/**
 * Step building blocks shared by every Meetings tour
 * (``list.ts``, ``detail.ts``, ``combined.ts``).
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

export const hasDetailMounted = (): boolean =>
  has('[data-tour-id="meeting-detail-header-actions"]');

// ─── List-side ───────────────────────────────────────────────────────────────

export const newMeetingStep: TourStep = {
  id: 'new-meeting',
  target: '[data-tour-id="meetings-new"]',
  placement: 'left',
  title: 'Schedule a meeting',
  body:
    'Click here to create a new meeting — capture agenda, attendees, ' +
    'location, and the meeting link before it happens.',
};

export const filtersStep: TourStep = {
  id: 'filters',
  target: '[data-tour-id="meetings-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Find a meeting',
  body:
    'Search agendas and summaries, or filter by status (scheduled, ' +
    'completed, cancelled) and meeting type.',
};

export const listTableStep: TourStep = {
  id: 'table',
  target: '[data-tour-id="meetings-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open a meeting',
  body: 'Each row is one meeting. Click any row to open its full minutes.',
};

export const combinedTableStep: TourStep = {
  id: 'open-a-meeting',
  target: '[data-tour-id="meetings-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open a meeting',
  body: 'Click any row to open it. Press Next and we\u2019ll open the first one for you.',
  onBeforeNext: async () => {
    clickPreferReact('[data-tour-id="meetings-table"] tbody tr');
    await waitForCondition(hasDetailMounted, 4000, 'row click → detail page');
  },
  autoAdvanceWhen: () => hasDetailMounted(),
};

// ─── Detail-side ─────────────────────────────────────────────────────────────

export const headerActionsStep: TourStep = {
  id: 'header-actions',
  target: '[data-tour-id="meeting-detail-header-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Status & actions',
  body:
    'Track the meeting\u2019s status (Scheduled, In Progress, Completed) and ' +
    'edit or delete it from here.',
};

export const metadataStep: TourStep = {
  id: 'metadata',
  target: '[data-tour-id="meeting-detail-metadata"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'When, where, who',
  body:
    'When the meeting is scheduled, how long, where it\u2019s held, who\u2019s ' +
    'organizing it — plus a one-click link to join if it\u2019s remote.',
};

export const attendeesStep: TourStep = {
  id: 'attendees',
  target: '[data-tour-id="meeting-detail-attendees"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Attendees',
  body:
    'Internal team members invited from your project, plus any external ' +
    'attendees you noted manually.',
};

export const contentStep: TourStep = {
  id: 'content',
  target: '[data-tour-id="meeting-detail-content"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Agenda, minutes, decisions, action items',
  body:
    'Keep the running record here — what you planned to cover, what you ' +
    'actually discussed, the decisions made, and the action items that came out of it.',
};
